import React, { createContext, useContext, useEffect, useState } from 'react'
import { useCatalog } from '~app/components/pages/SupportedDevices/components/Catalog/hooks/useCatalog'

type CountrySelectionContextProviderProps = {
  children: React.ReactNode
}
const getCookie = (name: string) => {
  const cookies = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${name}=`))
  return cookies ? cookies.split('=')[1] : null
}

export type CountrySelectionContext = {
  selectedCountry: string | null
  handleCountrySelection: (countryCode: string) => void
  isLoading: boolean
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  wwstCountryCodes: string[]
  regionNames: Intl.DisplayNames
}

export const CountrySelectionContext =
  createContext<CountrySelectionContext | null>(null)

export default function CountrySelectionContextProvider({
  children,
}: CountrySelectionContextProviderProps): JSX.Element {
  const regionNames = new Intl.DisplayNames(['en'], { type: 'region' })
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { state } = useCatalog()

  const COUNTRY_SELECTION_SESSION_KEY = 'countrySelection'

  useEffect(() => {
    // When a user changes the selected country, it is stored in sessionStorage and
    // that value should be used if available (on page refresh).
    //
    // Because sessionStorage is localized to a single browser tab, when the website is
    // opened in a new tab, there will not be a sessionStorage value, and the cookie value
    // will be used
    const storedCountrySelection = sessionStorage.getItem(
      COUNTRY_SELECTION_SESSION_KEY
    )

    if (storedCountrySelection !== null) {
      setSelectedCountry(storedCountrySelection)
      return
    }

    // If country cookie is set, use that as the selectedCountry value
    // The country cookie is set via the partners-lambda-origin-* lambda@Edge functions
    //    partners-lambda-origin-request  -> Gets the viewer's country from CloudFront and store in the request header
    //    partners-lambda-origin-response -> Gets the country from the request header, then sets the "country" cookie
    const countryValue = getCookie('country')

    if (countryValue && isValidCountrySelection(countryValue)) {
      setSelectedCountry(countryValue)
    } else {
      setSelectedCountry('')
    }
  }, [])

  const isValidCountrySelection = (countryCode: string) => {
    return state.brandCountryCodes.has(countryCode)
  }

  const handleCountrySelection = (countryCode: string) => {
    sessionStorage.setItem(COUNTRY_SELECTION_SESSION_KEY, countryCode)
    setSelectedCountry(countryCode)
  }

  // Ref for why these country codes are being used: https://smartthings.atlassian.net/browse/FEND-6994?focusedCommentId=1550938
  const wwstCountryCodes = [
    'AG',
    'AI',
    'AR',
    'AW',
    'BB',
    'BM',
    'BO',
    'BR',
    'BS',
    'BZ',
    'CA',
    'CL',
    'CO',
    'CR',
    'DM',
    'DO',
    'EC',
    'GD',
    'GF',
    'GT',
    'GY',
    'HN',
    'HT',
    'JM',
    'KN',
    'KY',
    'LC',
    'MS',
    'MX',
    'NI',
    'PA',
    'PE',
    'PR',
    'PY',
    'SR',
    'SV',
    'TC',
    'TT',
    'US',
    'UY',
    'VC',
    'VE',
    'VG',
    'AD',
    'AE',
    'AL',
    'AT',
    'BA',
    'BE',
    'BG',
    'CH',
    'CY',
    'CZ',
    'DE',
    'DK',
    'EE',
    'ES',
    'FI',
    'FO',
    'FR',
    'GB',
    'GI',
    'GL',
    'GP',
    'GR',
    'HR',
    'HU',
    'IE',
    'IL',
    'IS',
    'IT',
    'LI',
    'LT',
    'LU',
    'LV',
    'MC',
    'ME',
    'MT',
    'NL',
    'NO',
    'PL',
    'PT',
    'QA',
    'RO',
    'RS',
    'SE',
    'SI',
    'SK',
    'SM',
    'TR',
    'VA',
    'ZA',
    'AU',
    'BD',
    'BN',
    'BT',
    'CK',
    'FJ',
    'FM',
    'HK',
    'ID',
    'IN',
    'JP',
    'KH',
    'KI',
    'KR',
    'LA',
    'LK',
    'MH',
    'MM',
    'MN',
    'MO',
    'MV',
    'MY',
    'NC',
    'NP',
    'NR',
    'NU',
    'NZ',
    'PF',
    'PG',
    'PH',
    'PW',
    'SB',
    'SG',
    'TH',
    'TL',
    'TO',
    'TV',
    'TW',
    'VN',
    'VU',
    'WS',
  ]

  return (
    <CountrySelectionContext.Provider
      value={{
        selectedCountry,
        handleCountrySelection,
        isLoading,
        setIsLoading,
        wwstCountryCodes,
        regionNames,
      }}
    >
      {children}
    </CountrySelectionContext.Provider>
  )
}

export function useCountrySelectionContext(): CountrySelectionContext {
  const context = useContext(CountrySelectionContext)
  if (!context) {
    throw new Error(
      'useCountrySelectionContext must be used within a CountrySelectionContextProvider'
    )
  }
  return context
}
